/**
 * Superficies e conteudo: `<Badge />`, `<Card />`, `<Skeleton />`, `<EmptyState />` e
 * `<ErrorState />`. DESIGN_SYSTEM §4.3, §4.5, §4.7, §6.6, §7.9, §7.10, §7.11.
 */

import type { ReactNode } from 'react';

import { t } from '../content';
import { Button } from './Button';
import { Icon, type IconName, Pictogram, type PictogramName } from './Icon';
import { cx } from './util';

/** §4.0 — os sete tons semanticos. Nao existe `success`: verde = marca (§2.2.1). */
export type Tone = 'neutral' | 'brand' | 'danger' | 'warning' | 'info' | 'promo' | 'clube';

// ---------------------------------------------------------------------------
// Badge — §4.5
// ---------------------------------------------------------------------------

export interface BadgeProps {
  readonly tone: Tone;
  readonly children: string;
  readonly icon?: IconName;
  readonly variant?: 'soft' | 'solid' | 'outline';
  readonly className?: string;
}

/**
 * O selo e **texto**, nunca `title` (§5.8). `title` nao aparece em toque e nao e lido de forma
 * confiavel; um selo que so existe no hover e um selo que metade do publico nunca ve.
 */
export function Badge({ tone, children, icon, variant = 'soft', className }: BadgeProps): ReactNode {
  return (
    <span className={cx('balcao-badge', `balcao-badge--${tone}`, `balcao-badge--${variant}`, className)}>
      {icon === undefined ? null : <Icon name={icon} size={16} />}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Card — §4.3, §6.6
// ---------------------------------------------------------------------------

export interface CardProps {
  readonly as?: 'article' | 'div' | 'li';
  /** §2.9 — card em lista usa borda (elevacao 0), nao sombra: 20 box-shadows custam repaint. */
  readonly elevation?: 0 | 1 | 2;
  readonly interactive?: boolean;
  readonly tone?: Tone;
  readonly className?: string;
  readonly children?: ReactNode;
}

export function Card({
  as: Elemento = 'div',
  elevation = 0,
  interactive = false,
  tone,
  className,
  children,
}: CardProps): ReactNode {
  return (
    <Elemento
      className={cx(
        'balcao-card',
        elevation > 0 && `balcao-card--elev-${elevation}`,
        interactive && 'balcao-card--interactive',
        tone !== undefined && `balcao-card--${tone}`,
        className,
      )}
    >
      {children}
    </Elemento>
  );
}

// ---------------------------------------------------------------------------
// Skeleton — §4.7, §7.9
// ---------------------------------------------------------------------------

export type SkeletonShape = 'text' | 'title' | 'thumb' | 'card' | 'row' | 'price';

export interface SkeletonProps {
  readonly shape: SkeletonShape;
  readonly lines?: number;
  readonly width?: string;
  readonly className?: string;
}

/**
 * **Skeleton nunca desenha um valor** (§4.7). A barra de preco tem largura fixa de 96 px e cor
 * de superficie: nao pode ser confundida com um preco real.
 *
 * §7.9 — skeleton so aparece apos **400 ms** de espera, e so quando NAO ha dado em cache. Com
 * dado antigo em cache, mostra-se o dado antigo mais `StaleDataNote`: em rede ruim isso e
 * melhor que skeleton. O atraso e decisao de quem chama (o dado esta la, nao aqui); o
 * componente e `aria-hidden` porque uma barra cinza nao tem nada a dizer ao leitor.
 */
export function Skeleton({ shape, lines = 1, width, className }: SkeletonProps): ReactNode {
  const barras = Array.from({ length: Math.max(1, lines) }, (_, indice) => indice);
  return (
    <span aria-hidden className={cx('balcao-skeleton-group', className)}>
      {barras.map((indice) => (
        <span
          key={indice}
          className={cx('balcao-skeleton', `balcao-skeleton--${shape}`)}
          style={width === undefined ? undefined : { width }}
        />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// EmptyState e ErrorState — §4.7, §7.10, §7.11
// ---------------------------------------------------------------------------

export interface AcaoDeEstado {
  readonly label: string;
  readonly onPress: () => void;
}

export interface EmptyStateProps {
  /** Do conjunto proprio (§1.5), nao icone de UI. */
  readonly pictogram: PictogramName;
  /** O que aconteceu, em uma frase. */
  readonly title: string;
  /** Por que, em uma frase. */
  readonly description?: string;
  readonly primaryAction?: AcaoDeEstado;
  /**
   * §7.10 — quase sempre "Pedir pelo WhatsApp". Regra especifica deste projeto: no ano 1 o
   * catalogo estara incompleto e o WhatsApp e o canal que funciona.
   */
  readonly secondaryAction?: AcaoDeEstado;
  readonly className?: string;
}

export function EmptyState({
  pictogram,
  title,
  description,
  primaryAction,
  secondaryAction,
  className,
}: EmptyStateProps): ReactNode {
  return (
    <div className={cx('balcao-state', className)}>
      <Pictogram name={pictogram} size={48} />
      <p className="balcao-state__title">{title}</p>
      {description === undefined ? null : <p className="balcao-state__description">{description}</p>}
      {primaryAction === undefined && secondaryAction === undefined ? null : (
        <div className="balcao-state__actions">
          {primaryAction === undefined ? null : (
            <Button variant="primary" fullWidth onClick={primaryAction.onPress}>
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction === undefined ? null : (
            <Button variant="secondary" fullWidth onClick={secondaryAction.onPress}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export interface ErrorStateProps extends EmptyStateProps {
  /** Codigo curto, copiavel, em caption. Ex.: "PIX-EXP-02". E o que o atendente pede. */
  readonly code?: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}

/**
 * §7.11 — todo erro responde a quatro perguntas: **o que aconteceu · o dinheiro esta seguro? ·
 * o que fazer agora · como falar com gente**.
 *
 * Nunca exibir stack trace, "Error 500" nem o nome do gateway (§7.11, §9.2).
 *
 * `role="alert"` fica de fora de proposito: um `ErrorState` e uma TELA, e o leitor ja vai
 * chegar nela pela navegacao. `assertive` e reservado a tres casos (§3.5), e uma tela inteira
 * anunciada de forma assertiva atropela a leitura do proprio conteudo dela.
 */
export function ErrorState({ code, onRetry, retryLabel, className, ...resto }: ErrorStateProps): ReactNode {
  return (
    <div className={cx('balcao-state', className)}>
      <EmptyState
        {...resto}
        {...(onRetry === undefined
          ? {}
          : { primaryAction: { label: retryLabel ?? t('estado.tentarDeNovo'), onPress: onRetry } })}
      />
      {code === undefined ? null : (
        <span className="balcao-state__code">{`${t('estado.codigo')}: ${code}`}</span>
      )}
    </div>
  );
}
